import { getStatusContratoPorCodigo } from '../../../modules/types/StatusContratoEnum';
import { getTipoContratoPorCodigo } from '../../../modules/types/TipoContratoEnum';
import { getTipoEnvolvimentoLabelPorSlug } from '../../../modules/types/TipoEnvolvimentoEnum';
import { EnumOption } from '../../../modules/types/types';
import { valoresExibidosOffCanvasContratos as valoresExibidosObjetos } from '../../../pages/daf/ProcessosAquisitivos/contratos/objeto/auxiliarObjeto';
import { valoresExibidosOffCanvasContratos as valoresExibidosParcelas } from '../../../pages/daf/ProcessosAquisitivos/contratos/parcela/auxiliarParcela';
import { getAllObjetoContratos } from '../../../services/contratos/objetoContratoService';
import { getAllParcelaContratos } from '../../../services/contratos/parcelaContratoService';
import { ExportSheet } from '../core';
import { baseLogColumns, DataPreparer } from '../factory';
import {
  addFiscaisAquisicaoSheet,
  addPartesEnvolvidasSheet,
  addResponsaveisAquisicaoSheet,
  addTermosAditivosSheet,
  addTramitacoesSeiSheet,
  fetchGenericEntityData,
} from './auxiliar_componentes';

export class ContratoExportPreparer implements DataPreparer {
  private modulo = 'contrato';
  /**
   * Busca todos os dados relacionados aos Contratos selecionados
   */
  private async fetchRelatedData(contratoIds: number[]): Promise<any> {
    if (contratoIds.length === 0) return {};

    try {
      const [allObjetos, allParcelas] = await Promise.all([
        this.fetchContratoObjeto(),
        this.fetchContratoParcela(),
      ]);
      const objetos = this.filterByContratoIds(allObjetos, contratoIds);
      const parcelas = this.filterByContratoIds(allParcelas, contratoIds);

      const [
        partesEnvolvidas,
        fiscaisContrato,
        responsaveisContrato,
        termosAditivos,
        tramitacoesSei,
      ] = await Promise.all([
        fetchGenericEntityData('parteenvolvida', this.modulo, contratoIds),
        fetchGenericEntityData('fiscalaquisicao', this.modulo, contratoIds),
        fetchGenericEntityData(
          'responsavelaquisicao',
          this.modulo,
          contratoIds,
        ),
        fetchGenericEntityData('termoaditivo', this.modulo, contratoIds),
        fetchGenericEntityData('tramitacoes', this.modulo, contratoIds),
      ]);

      return {
        objetos,
        parcelas,
        partesEnvolvidas,
        fiscaisContrato,
        responsaveisContrato,
        termosAditivos,
        tramitacoesSei,
      };
    } catch (error) {
      console.error('Erro ao buscar dados relacionados ao Contrato:', error);
      return {};
    }
  }

  private async fetchContratoObjeto(): Promise<any[]> {
    try {
      const result = await getAllObjetoContratos();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Erro ao buscar objetos do Contrato:', error);
      return [];
    }
  }

  private async fetchContratoParcela(): Promise<any[]> {
    try {
      const result = await getAllParcelaContratos();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Erro ao buscar parcelas do Contrato:', error);
      return [];
    }
  }

  private filterByContratoIds(data: any[], contratosIds: number[]): any[] {
    if (!Array.isArray(data) || data.length === 0) return [];

    return data.filter((item) => {
      // Acessa o id do Contrato através de duas formas possíveis
      const contratoId = item.contrato?.id || item.contratoId;
      return contratosIds.includes(contratoId);
    });
  }

  async prepareForExport(
    selectedContratos: any[],
    additionalData?: {
      componenteAf?: EnumOption[];
      statusArp?: EnumOption[];
      tipoContrato?: EnumOption[];
      leiLicitacao?: EnumOption[];
    },
  ): Promise<ExportSheet[]> {
    const sheets: ExportSheet[] = [];
    const contratoIds = selectedContratos.map((c) => c.id);

    const {
      componenteAf = [],
      statusArp = [],
      tipoContrato = [],
      leiLicitacao = [],
    } = additionalData || {};

    // 1. Sheet principal - Contratos
    sheets.push({
      name: 'Contratos',
      data: selectedContratos.map((contrato) =>
        this.transformContrato(
          contrato,
          componenteAf,
          statusArp,
          tipoContrato,
          leiLicitacao,
        ),
      ),
      columns: {
        ...baseLogColumns,
        numeroSeiContrato: 'Número SEI',
        numeroContrato: 'Nº Contrato',
        statusContrato: 'Status',
        tipoContrato: 'Tipo',
        tipoEnvolvimento: 'Envolvimento',
        leiLicitacao: 'Lei de Licitação',
        componenteAf: 'Componente AF',
        fornecedor: 'Fornecedor',
        denominacaoGenerica: 'Denominação Genérica',
        dataPublicacao: 'Publicação',
        dataVigencia: 'Vigência',
      },
    });

    // 2. Buscar dados relacionados
    const relatedData = await this.fetchRelatedData(contratoIds);

    // 3. Adicionar sheets dos dados relacionados
    this.addObjetosSheet(sheets, relatedData.objetos);
    this.addParcelasSheet(sheets, relatedData.parcelas);
    addPartesEnvolvidasSheet(sheets, this.modulo, relatedData.partesEnvolvidas);
    addFiscaisAquisicaoSheet(sheets, this.modulo, relatedData.fiscaisContrato);
    addResponsaveisAquisicaoSheet(
      sheets,
      this.modulo,
      relatedData.responsaveisContrato,
    );
    addTermosAditivosSheet(sheets, this.modulo, relatedData.termosAditivos);
    addTramitacoesSeiSheet(sheets, this.modulo, relatedData.tramitacoesSei);

    return sheets;
  }

  /**
   * Transformação principal do contrato
   */
  private transformContrato = (
    contrato: any,
    componenteAf: EnumOption[],
    statusArp: EnumOption[],
    tipoContrato: EnumOption[],
    leiLicitacao: EnumOption[],
  ) => {
    return {
      //logs
      id: contrato.id,
      usuarioRegistro: contrato.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: contrato.dataRegistro,
      dataUltimaAtualizacao: contrato.dataUltimaAtualizacao,
      usuarioAtualizacao: contrato.usuarioAtualizacao?.nomeCompleto || 'N/A',
      usuarioUltimaVisualizacao:
        contrato.usuarioUltimaVisualizacao?.nomeCompleto || 'N/A',
      nEdicoes: contrato.nEdicoes || 0,
      nVisualizacoes: contrato.nVisualizacoes || 0,
      nVisualizadores: contrato.nVisualizadores || 0,

      //módulo
      numeroSeiContrato: contrato.numeroSeiContrato || 'N/A',
      numeroContrato: contrato.numeroContrato || 'N/A',
      statusContrato:
        statusArp[getStatusContratoPorCodigo(contrato.statusContrato) || 0]
          ?.label || '',
      tipoContrato:
        tipoContrato[getTipoContratoPorCodigo(contrato.tipoContrato) || 0]
          ?.label || contrato.tipoContrato,
      tipoEnvolvimento:
        getTipoEnvolvimentoLabelPorSlug(contrato.tipoEnvolvimento) || 'N/A',
      leiLicitacao:
        leiLicitacao[contrato.leiLicitacao]?.label || contrato.leiLicitacao,
      componenteAf:
        componenteAf[contrato.componenteAf]?.label || contrato.componenteAf,
      fornecedor: contrato.fornecedor?.razaoSocial || 'N/A',
      denominacaoGenerica:
        contrato.denominacaoGenerica?.denominacaoGenerica || 'N/A',
      dataPublicacao: contrato.dataPublicacao,
      dataVigencia: contrato.dataVigencia,

      //observações
      observacoes: contrato.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Objetos se houver dados
   */
  private addObjetosSheet(sheets: ExportSheet[], objetos?: any[]) {
    if (!objetos?.length) return;

    sheets.push({
      name: 'Objetos de Trabalho',
      data: objetos.map(this.transformObjeto),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosObjetos,
        produtoDAF: 'Produto Farmacêutico',
        contratoId: 'ID CONTRATO',
      },
    });
  }

  private transformObjeto = (objeto: any) => {
    return {
      //logs
      id: objeto.id,
      usuarioRegistro: objeto.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: objeto.dataRegistro,
      dataUltimaAtualizacao: objeto.dataUltimaAtualizacao,
      usuarioAtualizacao: objeto.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: objeto.nEdicoes || 0,
      nVisualizacoes: objeto.nVisualizacoes || 0,
      nVisualizadores: objeto.nVisualizadores || 0,

      //Dados do Objeto
      contratoId: objeto.contrato.id || 'N/A',
      produtoDAF: objeto.produtoDAF.produtoFarmaceutico,
      nObjeto: objeto.nObjeto || 'N/A',
      fatorEmbalagem: objeto.fatorEmbalagem,
      valorUnitario: parseFloat(objeto.valorUnitario) ?? '-',

      observacoes: objeto.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Parcelas se houver dados
   */
  private addParcelasSheet(sheets: ExportSheet[], parcelas?: any[]) {
    if (!parcelas?.length) return;

    sheets.push({
      name: 'Parcelas de Trabalho',
      data: parcelas.map(this.transformParcela),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosParcelas,
        produtoDAF: 'Produto Farmacêutico',
        contratoId: 'ID CONTRATO',
      },
    });
  }

  private transformParcela = (parcela: any) => {
    return {
      //logs
      id: parcela.id,
      usuarioRegistro: parcela.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: parcela.dataRegistro,
      dataUltimaAtualizacao: parcela.dataUltimaAtualizacao,
      usuarioAtualizacao: parcela.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: parcela.nEdicoes || 0,
      nVisualizacoes: parcela.nVisualizacoes || 0,
      nVisualizadores: parcela.nVisualizadores || 0,

      //Dados da Parcela
      contratoId: parcela.contrato?.id || 'N/A',
      nParcela: parcela.nParcela || 'N/A',
      nObjeto: parcela.objeto?.nObjeto || 'N/A',
      produtoDAF: parcela.objeto?.produtoDAF?.produtoFarmaceutico ?? 'N/A',
      valorParcela: parseFloat(parcela.valorParcela) ?? '-',
      qtdTotal: parcela.qtdTotal ?? '-',
      valorTotal: parseFloat(parcela.valorTotal) ?? '-',

      observacoes: parcela.observacoes || '',
    };
  };
}
